import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'

interface BlogPost {
  id: number
  title: string
  category: string
  readTime: string
  author?: string
  date?: string
  content: string
}

interface BlogModalProps {
  post: BlogPost | null
  isOpen: boolean
  onClose: () => void
}

const BlogModal: React.FC<BlogModalProps> = ({ post, isOpen, onClose }) => {
  const navigate = useNavigate()

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen || !post) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div 
      className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm'
      onClick={handleBackdropClick}
    >
      <div 
        className='bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex-shrink-0 border-b border-gray-200 px-6 py-4 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='bg-[#FEE4E2] rounded-lg px-3 py-1'>
              <span className='text-sm font-medium text-gray-700 text-start'>{post.category}</span>
            </div>
            <span className='text-sm text-gray-500 text-start'>{post.readTime}</span>
          </div>
          <button
            onClick={onClose}
            className='p-2 hover:bg-gray-100 rounded-full transition-colors'
            aria-label='Close modal'
          >
            <X className='h-5 w-5 text-gray-600' />
          </button>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-6 py-6'>
          <h1 className='text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-start'>{post.title}</h1>
          
          {(post.author || post.date) && (
            <div className='flex items-center gap-4 mb-6 text-sm text-gray-600 text-start'>
              {post.author && <span>By {post.author}</span>}
              {post.date && <span>• {post.date}</span>}
            </div>
          )}

          <div className='prose prose-lg max-w-none'>
            <div className='text-gray-700 leading-relaxed whitespace-pre-line text-start'>
              {post.content}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='flex-shrink-0 border-t border-gray-200 px-6 py-4 flex items-center justify-between'>
          <button
            onClick={onClose}
            className='text-sm text-gray-600 hover:text-gray-900 transition-colors'
          >
            Close
          </button>
          <button
            onClick={() => {
              onClose()
              navigate('/sign-up')
            }}
            className='bg-[#001B38] text-white px-6 py-2 rounded-lg hover:bg-[#002a50] transition-colors text-sm font-medium'
          >
            Get Started with BritFiler
          </button>
        </div>
      </div>
    </div>
  )
}

export default BlogModal

